import React, {useState, useEffect, createRef, useCallback} from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    FormHelperText,
    Checkbox,
    TextField,
    Toolbar,
    Typography,
    Tooltip,
    Button,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Select,
    MenuItem,
    ClickAwayListener,
} from '@material-ui/core';
import get from 'lodash.get';
import {lighten, makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import moment from 'moment';
import {Controller, useFormContext} from 'react-hook-form';
import {isObject} from 'utils/objects';

const PrettyPrintYMD = props => {
    const dataIn = JSON.parse(props.data);
    let data = {};
    if (isObject(dataIn)) {
        data = dataIn;
    } else if (dataIn === '') {
        data = {};
    } else {
        data = JSON.parse(dataIn.replaceAll(/'/g, '"')) || {};
    }
    const months = Object.keys(data);

    // I feel like there's a more elegant way to write this? def optin to suggestions
    return (
        <React.Fragment>
            {months
                .filter(month => !data[month].length)
                .map(month => (
                    <React.Fragment key={`month-${month}`}>
                        <Typography variant='body1'>
                            {moment.months()[month]}:
                        </Typography>
                        {Object.keys(data[month]).map(year => (
                            <Box paddingLeft='1em' key={`box-${year}`}>
                                <Typography variant='body1'>
                                    {year}: {data[month][year]}
                                </Typography>
                            </Box>
                        ))}
                    </React.Fragment>
                ))}
        </React.Fragment>
    );
};

const useFieldStyles = makeStyles({
    root: {
        transformOrigin: 'top right',
    },
    formControl: {
        left: 'auto',
        right: 0,
    },
});
const useStyles = makeStyles(theme => ({
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    grow: {
        flexGrow: 1,
    },
    title: {
        flex: '1 1 100%',
    },
    numberInput: {
        textAlign: 'right',
    },
}));

const SeaService = ({name, error, value, onChange}) => {
    const fieldClasses = useFieldStyles();
    const classes = useStyles();

    // UI state
    const [data, setData] = useState(value || {});
    const [month, setMonth] = useState(new Date().getMonth());
    const [viewData, setViewData] = useState({});
    const [selected, setSelected] = useState([]);
    const [customError, setCustomError] = useState(null);
    const totalNum = Object.keys(viewData).length;
    const numSelected = selected.length;

    // Input state
    const [year, setYear] = useState('');
    const [days, setDays] = useState('');
    const [touched, setTouched] = useState(false);
    const inputRow = createRef();
    const yearInput = createRef();
    const container = createRef();

    const incrementMonth = () => {
        handleAdd();
        setMonth((month + 1) % 12);
    };

    const decrementMonth = () => {
        handleAdd();
        setMonth((12 + month - 1) % 12);
    };

    useEffect(() => {
        setViewData(data[month] || {});
    }, [data, month]);

    const isSelected = thisYear => selected.indexOf(thisYear) !== -1;

    const handleRowClick = (event, thisYear) => {
        const index = selected.indexOf(thisYear);
        let newSelected = [];

        if (index === -1) {
            newSelected = selected.concat(thisYear);
        } else if (index === 0) {
            newSelected = selected.slice(1);
        } else if (index === selected.length - 1) {
            newSelected = selected.slice(0, -1);
        } else {
            newSelected = newSelected.concat(
                selected.slice(0, index),
                selected.slice(index + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            setSelected(Object.keys(viewData));
        } else {
            setSelected([]);
        }
    };

    const deleteSelected = () => {
        setCustomError(null);
        let newData = JSON.parse(JSON.stringify(data));
        selected.forEach(deletedYear => {
            delete newData[month][deletedYear];
            if (!Object.keys(newData[month]).length) {
                delete newData[month];
            }
        });

        setData(newData);
        onChange(newData);
        setSelected([]);
    };

    const trimMonth = monthIn => {
        const fullMonth = `0${monthIn}`;
        return fullMonth.substring(fullMonth.length - 2, fullMonth.length);
    };

    const handleAdd = useCallback(() => {
        // Support 4 or 2 char year
        const yearDigits = `${year}`.length;
        // only add if user entered both a 2 or 4 digit year, and days for that year
        if (parseInt(days) > 0 && [2, 4].includes(yearDigits)) {
            setCustomError(null);
            const fullYear = `20${year}`;
            const trimmedMonth = trimMonth(month + 1);
            const trimmedYear = fullYear.substring(
                fullYear.length - 4,
                fullYear.length
            );

            // check min date
            if (parseInt(trimmedYear) < 1900) {
                setCustomError(
                    'Dates must be within the last century, please.'
                );
                return false;
            }

            // check max date
            const newDateInFuture = moment().isBefore(
                `${trimmedYear}-${trimmedMonth}-01`,
                'day'
            );
            if (newDateInFuture) {
                setCustomError('Cannot enter dates in the future.');
                return false;
            }

            const monthData = data[month] || {};
            const yearData = monthData[trimmedYear] || 0;
            const dayData = parseInt(days) + yearData;
            const numDaysInMonth = moment(
                `${trimmedYear}-${trimmedMonth}`,
                'YYYY-MM'
            ).daysInMonth();
            // check exceeding possible days in the month
            if (dayData > numDaysInMonth) {
                setCustomError(
                    `Cannot enter more days than there were in ${
                        moment.months()[month]
                    } ${trimmedYear}. Maximum allowed is ${numDaysInMonth}`
                );
                return false;
            }

            // all checks passed. save state and update dependent component
            const newData = {
                ...data,
                [month]: {
                    ...data[month],
                    [trimmedYear]: yearData + parseInt(days),
                },
            };
            setData(newData);
            onChange(newData);
        }
        setYear('');
        setDays('');
    }, [data, days, month, onChange, year]);

    const handleClickAway = useCallback(() => {
        handleAdd();
    }, [handleAdd]);

    useEffect(() => {
        const inputRef = inputRow.current;
        const handleInput = e => {
            // Enter
            if (e.keyCode === 13) {
                handleClickAway();
            }
        };
        inputRef.addEventListener('keydown', handleInput);

        return () => {
            inputRef.removeEventListener('keydown', handleInput);
        };
    }, [handleClickAway, inputRow]);

    useEffect(() => {
        // Focus year input when data changes
        if (yearInput.current && touched) {
            yearInput.current.querySelector('input').focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalNum]);

    const firstClick = useCallback(
        e => {
            if (!totalNum && !touched) {
                yearInput.current?.querySelector('input').focus();
            }
        },
        [totalNum, touched, yearInput]
    );

    useEffect(() => {
        const containerRef = container.current;
        containerRef?.addEventListener('click', firstClick);
        return () => {
            containerRef?.removeEventListener('click', firstClick);
        };
    }, [container, firstClick]);

    return (
        <ClickAwayListener
            onClickAway={handleClickAway}
            mouseEvent='onMouseDown'
        >
            <FormControl ref={container}>
                <Toolbar
                    disableGutters
                    className={clsx(classes.root, {
                        [classes.highlight]: numSelected > 0,
                    })}
                >
                    {!numSelected && (
                        <>
                            <Tooltip title='Previous month'>
                                <IconButton
                                    edge='start'
                                    onClick={decrementMonth}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            </Tooltip>
                            <div className={classes.grow} />
                            <Select
                                displayEmpty
                                label='Month'
                                value={month}
                                onChange={event => {
                                    handleAdd();
                                    setMonth(event.target.value);
                                }}
                                data-cy='month-select'
                            >
                                {moment.months().map((month, index) => (
                                    <MenuItem key={month} value={index}>
                                        {month}
                                    </MenuItem>
                                ))}
                            </Select>
                            <div className={classes.grow} />
                            <Tooltip title='Next month'>
                                <IconButton edge='end' onClick={incrementMonth}>
                                    <NavigateNextIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {numSelected > 0 && (
                        <>
                            <Typography className={classes.title}>
                                {numSelected} selected
                            </Typography>
                            <Tooltip title='Delete'>
                                <IconButton
                                    aria-label='delete'
                                    data-cy='field-serviceDays-delete'
                                    onClick={deleteSelected}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Toolbar>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding='checkbox'>
                                    <Checkbox
                                        disabled={!totalNum}
                                        indeterminate={
                                            !totalNum ||
                                            (numSelected > 0 &&
                                                numSelected < totalNum)
                                        }
                                        checked={
                                            totalNum > 0 &&
                                            numSelected === totalNum
                                        }
                                        onChange={handleSelectAllClick}
                                        inputProps={{
                                            'aria-label':
                                                'select all service entries',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align='right'>Year</TableCell>
                                <TableCell align='right'>Days</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.keys(viewData).map(year => {
                                const rowIsSelected = isSelected(year);
                                return (
                                    <TableRow
                                        hover
                                        role='checkbox'
                                        onClick={event =>
                                            handleRowClick(event, year)
                                        }
                                        aria-checked={rowIsSelected}
                                        tabIndex={-1}
                                        key={year}
                                        selected={rowIsSelected}
                                    >
                                        <TableCell padding='checkbox'>
                                            <Checkbox
                                                checked={rowIsSelected}
                                                aria-label={`${moment.months(
                                                    month
                                                )} ${year}, ${
                                                    viewData[year]
                                                } days`}
                                                data-cy={`field-serviceDays-row-${year}`}
                                            />
                                        </TableCell>
                                        <TableCell align='right'>
                                            {year}
                                        </TableCell>
                                        <TableCell align='right'>
                                            {viewData[year]}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow ref={inputRow}>
                                <TableCell>
                                    <Tooltip title='Add'>
                                        <Button
                                            variant='contained'
                                            color='secondary'
                                            startIcon={<AddIcon />}
                                            size='small'
                                            data-cy={`form-field-${name}-add`}
                                            disabled={!days}
                                            onClick={handleAdd}
                                        >
                                            Add
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        ref={yearInput}
                                        name={`form-field-${name}-year`}
                                        inputProps={{
                                            className: classes.numberInput,
                                            'data-cy': `form-field-${name}-year`,
                                        }}
                                        value={year}
                                        onChange={e => setYear(e.target.value)}
                                        InputLabelProps={{
                                            classes: fieldClasses,
                                        }}
                                        type='tel'
                                        label='Year'
                                        onFocus={() => setTouched(true)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        inputProps={{
                                            className: classes.numberInput,
                                            'data-cy': `form-field-${name}-days`,
                                        }}
                                        value={days}
                                        onChange={e => setDays(e.target.value)}
                                        InputLabelProps={{
                                            classes: fieldClasses,
                                        }}
                                        type='tel'
                                        label='Days'
                                        onFocus={() => setTouched(true)}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <FormHelperText error>{customError}</FormHelperText>
            </FormControl>
        </ClickAwayListener>
    );
};

const SeaServiceField = ({label, name, required, onChange}) => {
    const {control, errors} = useFormContext();
    const error = get(errors, name);

    return (
        <FormControl error={!!error}>
            <FormLabel id={`${name}-label`} required={required}>
                {label}
            </FormLabel>
            <Controller
                render={p => (
                    <SeaService
                        name={p.name}
                        error={!!error}
                        onBlur={p.onBlur}
                        onChange={data => {
                            onChange(data);
                            p.onChange(data);
                        }}
                        value={p.value}
                    />
                )}
                name={name}
                control={control}
            />
            <FormHelperText>{error?.message}</FormHelperText>
        </FormControl>
    );
};

export default SeaServiceField;
export {PrettyPrintYMD};
